import React, { useState } from "react";
import Chart from "react-apexcharts";
import {
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaGlobe,
  FaServer,
  FaCogs,
  FaChartLine,
} from "react-icons/fa";
import useDashBoard from "../../customHooks/DashBoard/useDashBoard.js";

const Dashboard = () => {
  const {
    loader,
    dashboardData,
    serviceChartOptions,
    serviceChartSeries,
    providerChartSeries,
  } = useDashBoard();
  const [activeTab, setActiveTab] = useState("global");

  if (loader)
    return (
      <div className="p-10 text-center text-lg font-medium animate-pulse">
        Loading Analytics...
      </div>
    );
  if (!dashboardData)
    return (
      <div className="p-10 text-center text-lg font-medium">
        No Data Available
      </div>
    );

  // Function to get the correct data based on active tab
  const currentData =
    activeTab === "global"
      ? dashboardData
      : dashboardData.futuresoft_MSN_Provider_overview;

  const currentSeries =
    activeTab === "global" ? serviceChartSeries : providerChartSeries;

  const statsCards = [
    {
      label: "Total Tenants",
      value: currentData?.total_tenant_count ?? currentData?.tenant_count,
      icon: <FaUsers />,
      color: "#1E90FF",
    },
    {
      label: "Active",
      value: currentData?.active_tenant_count,
      icon: <FaCheckCircle />,
      color: "#32CD32",
    },
    {
      label: "Inactive",
      value: currentData?.inactive_tenant_count,
      icon: <FaTimesCircle />,
      color: "#FF4500",
    },
    {
      label: "Live",
      value: currentData?.live_tenant_count,
      icon: <FaGlobe />,
      color: "#FFA500",
    },
    {
      label: "Not Live",
      value: currentData?.not_live_tenant_count,
      icon: <FaServer />,
      color: "#6c757d",
    },
  ];

  return (
    <div className="container is-fullhd p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Tenant Analytics</h1>

        {/* --- Tab Switcher --- */}
        <div className="flex bg-gray-200 p-1 rounded-xl shadow-inner">
          <button
            onClick={() => setActiveTab("global")}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
              activeTab === "global"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Global Overview
          </button>
          <button
            onClick={() => setActiveTab("provider")}
            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
              activeTab === "provider"
                ? "bg-white text-purple-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            FutureSoft Provider
          </button>
        </div>
      </div>

      {/* --- Dynamic Content Area --- */}
      <div className="space-y-8 animate-in fade-in duration-500">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {statsCards.map((card, idx) => (
            <StatCard key={`${activeTab}-${idx}`} {...card} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ChartContainer
            title={`${
              activeTab === "global" ? "Global" : "Provider"
            } Service Distribution`}
          >
            {currentSeries[0].data.length > 0 ? (
              <Chart
                options={serviceChartOptions}
                series={currentSeries}
                type="bar"
                height={320}
              />
            ) : (
              <div className="h-[320px] flex items-center justify-center text-gray-400 italic">
                No Service Data
              </div>
            )}
          </ChartContainer>

          <ChartContainer title="Environment Breakdown">
            <EnvironmentTable
              data={currentData?.environment_wise_tenant_count}
            />
          </ChartContainer>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---

const StatCard = ({ label, value, icon, color }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4 transition-all hover:shadow-md">
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl"
      style={{ backgroundColor: color }}
    >
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-400 font-bold uppercase">{label}</p>
      <h3 className="text-2xl font-black text-gray-800">{value}</h3>
    </div>
  </div>
);

const ChartContainer = ({ title, children }) => (
  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
    <h5 className="font-bold mb-6 text-gray-700 flex items-center gap-2">
      <FaChartLine className="text-blue-500" /> {title}
    </h5>
    {children}
  </div>
);

const EnvironmentTable = ({ data }) => (
  <div className="overflow-hidden rounded-lg border border-gray-100">
    <table className="min-w-full divide-y divide-gray-100">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">
            Environment
          </th>
          <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">
            Tenant Count
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-50">
        {data?.map((env, i) => (
          <tr key={i} className="hover:bg-blue-50/30 transition-colors">
            <td className="px-6 py-4 text-sm font-semibold text-blue-600 capitalize">
              {env.environment_name}
            </td>
            <td className="px-6 py-4 text-sm text-right text-gray-900 font-bold">
              {env.tenant_count}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default Dashboard;
